import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { vehicles } from "@data/vehicles";
import RentalVehicleModal from "@/components/RentalVehicleModal";
import SEO from "@components/SEO";
import {
  createVehicleStructuredData,
  createBreadcrumbStructuredData,
} from "../utils/structuredData";
import type { Vehicle } from "@/types";

const VEHICLE_TYPE_META: Record<
  string,
  { title: string; description: string; image: string }
> = {
  cars: {
    title: "New Zealand Car Rental | Sedan, SUV, MPV & Van Hire",
    description:
      "New Zealand car rental — sedans, SUVs, MPVs and vans for self-drive NZ tours. Comprehensive insurance, 24/7 roadside support and bookable from Malaysia or Singapore.",
    image: "/assets/ikhlas-tours-rental-cars.jpg",
  },
  motorhomes: {
    title: "New Zealand Motorhome & Campervan Hire | 2, 4 & 6 Berth",
    description:
      "New Zealand motorhome and campervan hire in 2, 4 and 6-berth configurations. Self-contained, fully insured rentals for a freedom-style NZ road trip from Malaysia.",
    image: "/assets/motorhome-group-diagonal.jpg",
  },
};

const VehicleDetailPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicleType, setSelectedVehicleType] = useState("");

  const getTypeTitle = (vehicleType: string) => {
    switch (vehicleType) {
      case "cars":
        return "Cars";
      case "motorhomes":
        return "Motorhomes";
      default:
        return vehicleType.charAt(0).toUpperCase() + vehicleType.slice(1);
    }
  };

  const getTypeDescription = (vehicleType: string) => {
    switch (vehicleType) {
      case "cars":
        return "Comfortable and reliable cars perfect for city driving, road trips and exploring New Zealand's scenic routes. Fuel efficient and well maintained vehicles with comprehensive insurance. See below for the class of cars that we offer.";
      case "motorhomes":
        return "Fully equipped motorhomes offering the freedom to explore New Zealand at your own pace. Perfect for adventure seekers who want comfort and flexibility on the road.";
      default:
        return "Explore our wide selection of vehicles for your New Zealand adventure.";
    }
  };

  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      (type && type === "cars" && vehicle.type === "car") ||
      (type === "motorhomes" && vehicle.type === "motorhome"),
  );

  const VehicleCard: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
    const handleBookNow = () => {
      setSelectedVehicleType(vehicle.type);
      setIsModalOpen(true);
    };

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
        {/* Vehicle Image */}
        <div className="relative h-62 overflow-hidden">
          <img
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}${vehicle.images[0]}`}
            alt={vehicle.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {/* <div className="absolute top-4 right-4">
          <span
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              vehicle.availability
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {vehicle.availability ? "Available" : "Unavailable"}
          </span>
        </div> */}
        </div>

        {/* Vehicle Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-primaryText mb-2 group-hover:text-primary transition-colors">
            {vehicle.name}
          </h3>
          <p className="text-gray-600 mb-4">{vehicle.description}</p>

          {/* Vehicle Specs */}

          {/* Features */}
          {/* <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {vehicle.features.slice(0, 3).map((feature, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
              >
                {feature}
              </span>
            ))}
            {vehicle.features.length > 3 && (
              <span className="text-gray-500 text-xs">
                +{vehicle.features.length - 3} more
              </span>
            )}
          </div>
        </div> */}

          {/* Price and CTA */}
          <div className="flex items-center justify-between">
            {/* <div>
              <span className="text-gray-500 text-sm">From </span>
              <div>
                <span className="text-2xl font-bold text-primary">
                  ${vehicle.pricePerDay}
                </span>
                <span className="text-gray-500 text-sm">/day</span>
              </div>
            </div> */}
            <button
              onClick={handleBookNow}
              className="cursor-pointer bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!type || (type !== "cars" && type !== "motorhomes")) {
    return (
      <div className="py-16 text-center">
        <SEO
          title="Vehicle Type Not Found | Ikhlas Tours"
          description="The vehicle type you're looking for doesn't exist."
          path={`/vehicles/${type ?? ""}`}
          noIndex
        />
        <h1 className="text-2xl font-bold text-primaryText mb-4">
          Vehicle Type Not Found
        </h1>
        <p className="text-gray-600 mb-8">
          The vehicle type you're looking for doesn't exist.
        </p>
        <Link
          to="/vehicles"
          className="inline-block bg-primary text-primaryText px-6 py-3 rounded-md font-semibold hover:bg-secondary transition-all duration-300"
        >
          Back to Vehicles
        </Link>
      </div>
    );
  }

  const meta = VEHICLE_TYPE_META[type];
  const typeLabel = type === "motorhomes" ? "Motorhomes" : "Cars";

  return (
    <div>
      <SEO
        title={meta.title}
        description={meta.description}
        path={`/vehicles/${type}`}
        image={meta.image}
        structuredData={[
          ...filteredVehicles.map((v) => createVehicleStructuredData(v)),
          createBreadcrumbStructuredData([
            { name: "Home", url: "/" },
            { name: "Vehicles", url: "/vehicles" },
            { name: typeLabel, url: `/vehicles/${type}` },
          ]),
        ]}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            {getTypeTitle(type)}
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            {getTypeDescription(type)}
          </p>
        </div>
      </section>

      {/* Vehicles Description */}
      {/* <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            {type === "cars"
              ? "Well maintained rental cars offers comfort, reliability and great fuel efficiency. All vehicles come with comprehensive insurance and 24/7 roadside assistance for your peace of mind."
              : "Motorhomes and campervans offer the perfect blend of comfort and adventure. With fully equipped kitchens, comfortable sleeping areas and all the amenities you need for an unforgettable New Zealand road trip."}
          </p>
        </div>
      </section> */}

      {/* Vehicles Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredVehicles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No vehicles found for this type.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVehicles.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            {type === "cars"
              ? "Can't Find What You're Looking For?"
              : "Need Help Choosing?"}
          </h2>
          <p className="text-xl text-primaryText mb-8">
            {type === "cars"
              ? "We can help you find the perfect vehicle for your New Zealand adventure."
              : "Our team can help you select the perfect motorhome for your New Zealand adventure."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Contact Us
            </Link>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/64221841601?text=Hi! I'm interested in vehicle rental.",
                  "_blank",
                )
              }
              className="inline-block bg-whatsapp text-white px-8 py-3 rounded-md font-semibold hover:bg-green-600 transition-all duration-300"
            >
              WhatsApp Us
            </button>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <RentalVehicleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vehicleType={selectedVehicleType}
      />
    </div>
  );
};

export default VehicleDetailPage;
